import { useCallback, useMemo } from 'react';
import type { ApolloCache } from '@apollo/client';
import {
  useFolderNameFragment,
  useSetFolderColorsMutation,
} from '@frameio/graphql-client/generated';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import { writeFolderColors } from '../../operations/cache-ops/writeFolderColors';
import {
  FOLDER_COLOR_OPTIONS,
  type FolderColorKey,
} from '../constants/folderColors';
import TEXT from './generated/TEXT/useFolderColor';

TEXT.DEFINE({
  /** Error message shown when updating folder color fails */
  error: 'There was an issue updating the folder color',
});

export function useFolderColor(folderIds: string | string[]) {
  const ids = useMemo(
    () => (Array.isArray(folderIds) ? folderIds : [folderIds]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Array.isArray(folderIds) ? folderIds.join(',') : folderIds]
  );

  // Read color state from the first (or only) folder
  const { data: folder } = useFolderNameFragment(ids[0] ?? '');
  const [setFolderColors] = useSetFolderColorsMutation();
  const tryCatchHandler = useToastTryCatchHandler();

  const hexColor = folder?.userProvidedColor ?? null;
  const colorOption = hexColor
    ? FOLDER_COLOR_OPTIONS.find(
        (o) => o.hex.toLowerCase() === hexColor.toLowerCase()
      ) ?? null
    : null;
  const colorKey = colorOption?.key ?? null;

  const setColor = useCallback(
    (key: FolderColorKey | null) => {
      const hex = key
        ? FOLDER_COLOR_OPTIONS.find((o) => o.key === key)?.hex ?? null
        : null;

      const updateCache = (cache: ApolloCache<unknown>) =>
        writeFolderColors(cache, ids, hex);

      tryCatchHandler({
        attempt: () =>
          setFolderColors({
            variables: { input: { folderIds: ids, userProvidedColor: hex } },
            optimisticResponse: {
              setFolderColors: { __typename: 'SetFolderColorsPayload' },
            },
            update: updateCache,
          }),
        errorMessage: TEXT.error,
      });
    },
    [ids, setFolderColors, tryCatchHandler]
  );

  return {
    colorKey,
    colorToken: colorOption?.token ?? null,
    textToken: colorOption?.textToken ?? null,
    setColor,
  };
}
